<template>
  <form class="variant-options">
    <template v-if="colors.length > 0">
      <div class="flex justify-between">
        <KikProperty
          class="mb-2 mobile:text-sm"
          :name="$t('color')"
          :value="selectedColor"
        />
        <KikDrawer
          v-if="!hideChartSize && sizeChartContent"
          title="Size chart"
        >
          <template #button>
            <div class="cursor-pointer text-base mobile:text-sm">
              <KikIcon
                size="sm"
                icon="tape"
                class="mr-1 mt-1"
              />
              <span class="underline">{{ $t('Size chart') }}</span>
            </div>
          </template>
          <template #content>
            <div
              class="size-chart-tables"
              v-html="sizeChartContent"
            />
          </template>
        </KikDrawer>
      </div>
      <div
        v-if="colors.length > 0"
        class="variant-options__color-selection mb-6"
      >
        <label
          v-for="(color, i) in colors"
          :key="i"
          class="image-wrapper relative border-b-4 border-b-transparent hover:border-b-primary-700"
          :class="{ '!border-b-primary-700': selectedColor === color }"
        >
          <input
            v-model="selectedColor"
            type="radio"
            :value="color"
          >
          <NuxtImg
            preset="pdpThumb"
            :width="$device.isMobile ? 82 : 92"
            :height="$device.isMobile ? 98 : 112"
            :src="thumbnailColors[color]"
            :alt="color"
            class="relative mb-1 inline-block max-w-[92] border border-neutral-200 mobile:w-full"
            tabindex="0"
            @click="changeColor(color)"
            @keydown="e => keyHandler(e, [], () => changeColor(color))"
          />
          <NuxtImg
            v-if="selectedColor === color"
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            :alt="color"
            src="/img/check.svg"
            :width="$device.isMobile ? 24 : 28"
          />
        </label>
      </div>
    </template>
    <template v-if="$flags.hasnt('shop.catalog.enabled')">
      <template v-if="selectedSizes.length === 1">
        <KikProperty
          :name="$t('size')"
          :value="selectedSizes[0]"
        />
      </template>
      <SfSelect
        v-else
        v-model="selectedSize"
        :placeholder="$t('Select size')"
        :aria-label="$t('Select size')"
        :required="false"
        :disabled="colors.length === 0"
        size="lg"
        class="bg-white font-sans text-neutral-600"
        @input="changeVariant($event)"
      >
        <option
          v-for="size in selectedSizes"
          :key="size"
          :value="size"
          :disabled="!enableColorSelection(size)"
          class="disabled:text-neutral-200"
        >
          {{ size }}
          {{ enableColorSelection(size) ? '' : $t('not_available') }}
        </option>
      </SfSelect>
      <KikFormError
        v-if="!validSelectedSize"
        class="relative mt-1"
        :error="$t('Please select a size')"
      />
      <KikAlert
        v-if="showAlert"
        class="mt-3"
        type="warning"
        :has-border="true"
        :message="getMessage"
      />
      <div
        v-if="minQty > 1"
        class="mt-2 border border-secondary-200 bg-secondary-100 px-4 py-1 text-sm mobile:text-sm"
      >
        {{ $t('Minimum order quantity x pieces', { amount: minQty }) }}
      </div>
      <div class="mt-4 flex w-full gap-4">
        <KikButton
          id="product__add-to-cart-btn"
          :disabled="!validSelectedSize || soldOut || addToCartLoading || minQty === 0"
          class="w-full"
          size="lg"
          data-testid="product__add-to-cart-btn"
          @click="addToCart"
        >
          <SfLoaderCircular v-if="addToCartLoading" />
          <span v-else>{{ soldOut ? $t('Sold out') : $t('Add to cart') }}</span>
        </KikButton>

        <KikButton
          v-if="$flags.hasnt('enabledKiosk') && $flags.has('shoppinglist.wishlist')"
          :title="$t('Add')"
          variant="secondary"
          data-testid="product__wishlist-btn"
          :disabled="soldOut"
          size="lg"
          class="mobile:hidden"
          @click="handleToggleToWishlist({ ...product, sku })"
        >
          <component
            :is="isInWishlist({ ...product, sku }) ? SfIconFavoriteFilled : SfIconFavorite"
            size="sm"
            class="fill-secondary"
          />
        </KikButton>
      </div>
    </template>
  </form>
</template>

<script lang="ts" setup>
import {
  SfSelect, SfLoaderCircular, SfIconFavorite, SfIconFavoriteFilled,
} from '@storefront-ui/vue';

import { useAppLocale } from '~/composables/custom/useAppLocale';
import { useWishlist } from '~/composables/useWishlist';
import { useUser } from '~/composables/custom/useUser/useUser';
import { useTracking } from '~/composables/custom/useTracking';
import { useSizeChart } from '~/composables/custom/useProduct/useSizeChart';
import { useProduct } from '~/composables/custom/useProduct/useProduct';
import { useAlert } from '~/composables/custom/useProduct/useAlert';
import { useCart } from '~/composables/custom/useCart/useCart';
import cartGetters from '~/composables/getters/cartGetters';
import keyHandler from '~/helpers/a11y/keyHandler';
import { useSSR } from '~/composables/useSSR/useSSR';

interface Props {
  variants?: any[];
  sizes?: any;
  colors?: string[];
  isUniSize?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variants: () => [],
  sizes: () => {},
  colors: () => [],
  isUniSize: false,
});

const emit = defineEmits({
  'change-variant': (variant: any | undefined) => variant,
  'change-to-dummy-variant': (variant: any | undefined) => variant,
});

const route = useRoute();
const { $i18n, $flags } = useNuxtApp();
const { country } = useAppLocale();
const { trackAddToCart } = useTracking();
const { isAuthenticated } = useUser();
const { toggleLoginDrawer } = useUiState();
const { showAlert, resetAlert, setAlert } = useAlert();
const { cart, addItem, error: cartError } = useCart();

const {
  variants,
  sizes,
  colors,
  isUniSize,
} = toRefs(props);

const {
  handleSizeChartTable,
  hideChartSize,
  sizeChartContent,
} = useSizeChart();

const {
  sku,
  availability,
  selectedColor,
  minQty,
  maxQty,
  validSelectedSize,
  setValidSelectedSize,
  setSelectedSize,
  getProductToTrack,
  breadcrumbs,
  product,
} = useProduct(route.path);

const {
  toggleToWishlist,
  isInWishlist,
  addToQueue,
} = useWishlist();

const selectedSize = ref();
const firstVariantWithStock = computed(() => variants.value?.find((variant: any) => variant.availability.isOnStock && variant.attributes.online));
const anyVariantWithStock = computed(() => !!firstVariantWithStock.value);
const soldOut = computed(() => !anyVariantWithStock.value);

const { onSSR } = useSSR();

onSSR(async () => {
  await handleSizeChartTable();
});

onBeforeMount(() => {
  emit('change-to-dummy-variant', variants.value?.find(({ attributes, availability }) => attributes.color === selectedColor.value && availability.isOnStock));
});

const enableColorSelection = (size: string) => {
  const variant = variants.value?.find(({ attributes }) => attributes.size === size && attributes.color === selectedColor.value);
  return variant?.availability.isOnStock && variant.attributes.online;
};

// Keeps size selected on color change if its in stock for the new color - if not it switches back to unselected size
const changeColor = (color: string) => {
  const variant = variants.value?.find(({ attributes, availability }) => attributes.color === color && attributes.size === selectedSize.value && availability.isOnStock && attributes.online) ?? null;
  if (variant) {
    emit('change-variant', variant);
  }
  else {
    selectedSize.value = undefined;
    setSelectedSize(selectedSize.value);

    emit('change-to-dummy-variant', variants.value?.find(({ attributes, availability }) => attributes.color === color && availability.isOnStock));
  }
};

const changeVariant = (event: any) => {
  const size = event.target.value;
  emit('change-variant', variants.value.find(variant => variant.attributes.color === selectedColor.value && variant.attributes.size === size));
  setSelectedSize(size);
};

const selectedSizes = computed(() => sizes.value?.[selectedColor.value] ?? []);

const thumbnailColors = computed(() => variants.value.reduce((acc, current) => {
  const { attributes, images } = current;

  if (!attributes.color || !images?.[0]?.url) {
    return acc;
  }
  return {
    ...acc,
    [attributes.color]: images[0].url,
  };
}, {}));

const getMessage = computed(() => $i18n.t('The max. order quantity is', { count: maxQty.value ?? '__' }).toString());

const addToCartLoading = ref(false);
const addToCart = async () => {
  addToCartLoading.value = true;

  const quantity = minQty.value;

  if (!isUniSize.value && !selectedSize.value) {
    setValidSelectedSize(false);
    addToCartLoading.value = false;
    return;
  }

  if (quantity === 0) {
    return;
  }

  if (!isUniSize.value && !selectedSize.value) {
    setValidSelectedSize(false);
    addToCartLoading.value = false;
    return;
  }
  resetAlert();

  const qtyInCart = cartGetters.getItemQtyBySku(cart.value, sku.value);

  if (availability.value && qtyInCart + quantity > availability.value) {
    setAlert();
    addToCartLoading.value = false;
    return;
  }

  await addItem({
    countryCode: country,
    productSku: sku.value,
    quantity,
    customQuery: undefined,
    breadcrumbs: breadcrumbs.value,
  });

  // TODO: ADD TRACKING
  if (!cartError.value.addItem) {
    const productToTrack = getProductToTrack(quantity);
    trackAddToCart(productToTrack, { sku: sku.value, quantity });
  }

  addToCartLoading.value = false;
};

const handleToggleToWishlist = (wishlistProduct: unknown) => {
  if (isAuthenticated.value) {
    toggleToWishlist(wishlistProduct);
  }
  else {
    toggleLoginDrawer();
    addToQueue(wishlistProduct);
  }
};
</script>

<style lang="scss" scoped>
.variant-options {
  &__color-selection {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(82px, 1fr));
    grid-gap: 10px;

    .image-wrapper {
      display: inline-block;
      cursor: pointer;
    }

    label > input[type=radio] {
      display: none;
    }
  }

  &__size-chart-link {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;

    @media screen and (min-width: $tablet-min) {
      margin-top: 15px;
      margin-bottom: -25px;
    }
  }

  &__property {
    margin-bottom: 12px;

    @include for-tablet-and-desktop {
      margin-bottom: 15px;
    }

    &--size {
      margin-top: 12px;

      @include for-tablet-and-desktop {
        margin-top: 20px;
      }
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .alert {
    margin: 18px 0;
    font-size: 12px;
    line-height: 17px;
    letter-spacing: 0.21px;

    @media screen and (min-width: $desktop-min) {
      font-size: 14px;
      line-height: 19px;
      letter-spacing: 0.25px;
    }
  }

  .size-select {
    margin: 20px 0 9px 0;
    padding: 0;
    height: auto;

    @media screen and (min-width: $tablet-min) {
      margin: 20px 0;
    }

    &__option {
      color: rgb(25, 25, 25);
      font-size: 14px;
      font-weight: normal;
      line-height: 19px;
      letter-spacing: 0.44px;

      &:disabled {
        color: rgb(204, 204, 204)
      }
    }

    .active {
      cursor: pointer;
    }

    .inactive {
      color: rgb(204, 204, 204);
    }
  }
}

.product {
  &__add-to-cart {
    margin: 0;
    align-items: flex-start;
  }
}
</style>
